import $ from "jquery";
import * as preact from "preact";

import * as analytics from "analytics";
import * as iOSBridge from "iOSBridge";
import * as keyboardShortcuts from "keyboardShortcuts";
import * as localAPI from "localAPI";
import * as modalController from "modalController";
import * as notificationBar from "notificationBar";
import type { PreactPageProps } from "pages/PreactPage";
import * as pageTransitions from "pageTransitions";
import * as readerPageNavigation from "readerPageNavigation";
import * as readerReviewUtils from "readerReviewUtils";
import readlangClient from "readlangClient";
import { createSerialQueue } from "serialQueue";
import * as languageMap from "shared/languageMap";
import * as utils from "shared/utils";
import * as sidebarSettings from "sidebarExpandedSetting";
import * as signupModal from "signupModal";
import type ReaderTextStore from "stores/ReaderTextStore";
import type ReaderViewStore from "stores/ReaderViewStore";
import stores from "stores/stores";
import * as streakReminderPrompt from "streakReminderPrompt";
import type { SyncAudioControlsProps } from "SyncAudioControls";
import * as tts from "tts";
import type { FormattingOptionsProps } from "views/FormattingOptions";
import type { ReaderFooterState } from "views/ReaderFooterView";
import ReaderPageLayoutView from "views/ReaderPageLayoutView";
import type {
  DictionaryPaneState,
  SmartDictionaryPaneState,
} from "views/ReaderSidebarPanes";
import * as walkthrough from "walkthrough";
import * as browserTTSVoices from "../browserTTSVoices";
import { bottomSheetHeight, shiftToRevealWord } from "./readerBottomSheet";
import { createDictionaryManager } from "./readerPageDictionary";
import { createReaderPageLoader } from "./readerPageLoader";
import { createReadAloudController } from "./readerPageReadAloud";
import { createReaderPageSidebarEffects } from "./readerPageSidebarEffects";
import { ReaderSyncAudio } from "./readerPageSyncAudio";
import ReaderTextController from "./ReaderTextController";
import { elementForPosition } from "./readerTextLayout";
import {
  removeReaderWordPopUp,
  showGuideEntryPopUp,
  showReaderWordPopUp,
} from "./readerWordPopUp";

type State = {
  /** How far the text is moved up, to stay in view above the bottom sheet */
  bookShift?: number;
  /** Leaving a few lines of text in view above it (px) */
  bottomSheetHeight?: number;
  dictionaryPaneState?: DictionaryPaneState;
  errorMessageLines?: string[];
  footerState?: ReaderFooterState;
  formattingOptionsProps?: FormattingOptionsProps;
  mediaPanelHeight?: number;
  mediaPanelResizing?: boolean;
  pageData?: any;
  pageTextViewportNoFooter?: boolean;
  readerTextStore?: ReaderTextStore;
  readerViewStore?: ReaderViewStore;
  scrollViewCompleted?: boolean;
  selectedTabName?: string;
  showStreak?: boolean;
  sidebarHintText?: string;
  sidebarHintTitle?: string;
  sidebarHintVisible?: boolean;
  sideBarVisible?: boolean;
  smartDictionaryPaneState?: SmartDictionaryPaneState;
  streakHideUponCompletion?: boolean;
  /** The reminderReprompt2 streak popover is open (see streakReminderPrompt.ts). */
  streakReminderPrompt?: boolean;
  syncAudioControlsState?: SyncAudioControlsProps;
  titleBarVisible?: boolean;
  viewportWidth?: number;
};

const sideBarWidth = 425;
/** Below which the sidebar is a sheet over the bottom of the page */
const bottomSheetThreshold = 600;

const renderOptionsKey = (options: any) =>
  JSON.stringify({
    bookID: options?.bookID,
    endOfText: options?.endOfText,
    from: options?.from,
    scroll: options?.scroll,
    to: options?.to,
  });

export default class ReaderPageView extends preact.Component<
  PreactPageProps,
  State
> {
  private appRouter?: any;
  private dictionaryManager?: ReturnType<typeof createDictionaryManager>;
  /** Of the word last looked up, kept in view above the bottom sheet */
  private lookedUpPosition?: number;
  private pageData?: any;
  private mediaPlayerElement?: HTMLElement;
  private queueReaderPageLoad = createSerialQueue();
  private readerPageLoader?: ReturnType<typeof createReaderPageLoader>;
  private readerOptions?: any;
  private readerStreakShowDisposer?: () => void;
  private streakGoalMetDisposer?: () => void;
  private renderedCallback?: (renderedOptions?: any) => void;
  private readAloudController?: ReturnType<typeof createReadAloudController>;
  /**
   * Also mirrored into state for rendering; imperative consumers read this
   * field so page setup doesn't depend on the state commit having flushed
   * (a render pass can be aborted by an unrelated error - see the known
   * "Cycle detected" issue - and the field keeps setup resilient to that).
   */
  private readerTextStore?: ReaderTextStore;
  private selectedTabName?: string;
  private sidebarEffects?: ReturnType<typeof createReaderPageSidebarEffects>;
  private showSideBarCloseHint = false;
  private showingPageNavigationTooltip = false;
  private sideBarAutoToggled = false;
  private sideBarVisible = false;
  private syncAudio?: ReaderSyncAudio;
  private textController?: ReaderTextController;
  private unmounting = false;

  constructor(props: PreactPageProps) {
    super(props);
    this.state = {
      titleBarVisible:
        typeof window === "undefined"
          ? true
          : localStorage.getItem("reader.showTitleBar") !== "false",
      viewportWidth:
        typeof window === "undefined" ? undefined : window.innerWidth,
    };
  }

  componentDidMount() {
    this.textController = new ReaderTextController({
      getAppRouter: () => this.appRouter ?? this.props.appRouter,
      getDictionaryManager: () => this.dictionaryManager,
      getIsSyncAudioPlaying: () => Boolean(this.syncAudio?.status()?.playing),
      getOptions: () => this.readerOptions ?? this.props.options,
      getPageData: () => this.pageData,
      getReadAloudController: () => this.readAloudController,
      getReaderTextStore: () => this.readerTextStore,
      getReaderViewStore: () => this.state.readerViewStore,
      getRenderedCallback: () => this.renderedCallback,
      lookupGuideEntry: (text, actions) => {
        const entryIndex = stores.guide.findEntry(text, actions.neighborWord);
        if (entryIndex === undefined) {
          return false;
        }
        const entry = stores.guide.state.value.guide?.glossary[entryIndex];
        const readerViewStore = this.state.readerViewStore;
        if (!entry || !readerViewStore) {
          return false;
        }
        // Wait a tick so the selection's group span (data-p = range.from) has
        // rendered — it replaces the clicked token span at the same layout
        // position and is what the popover must anchor to
        window.setTimeout(() => {
          const targetElement = $(
            `.pageTextContent span[data-p="${actions.range.from}"]`
          );
          if (!targetElement.length) {
            actions.dismiss();
            return;
          }
          showGuideEntryPopUp({
            entry,
            targetElement,
            containerElement: $(".container"),
            readerViewStore,
            onDismiss: actions.dismiss,
            onTranslate: actions.translate,
            onOpenGuide: () => {
              actions.dismiss();
              stores.guide.selectEntry(entryIndex);
              this.openGuideTab();
            },
          });
        }, 0);
        return true;
      },
      maybeShowExplainNudge: (userWord) => {
        this.sidebarEffects?.maybeShowExplainNudge(userWord);
      },
      onPageRendered: () => {
        this.autoExpandSidebarIfAppropriate();
        // Continue Read Aloud playback if a sentence-end auto-paginate is
        // pending — see `resumeIfPending` for context.
        this.readAloudController?.resumeIfPending();
      },
      removeWordPopUp: removeReaderWordPopUp,
      setPageTextViewportNoFooter: (pageTextViewportNoFooter) => {
        this.setState({ pageTextViewportNoFooter });
      },
      setRenderedCallback: (renderedCallback) => {
        this.renderedCallback = renderedCallback;
      },
      showWordPopUp: showReaderWordPopUp,
      updateFooterState: this.updateFooterState,
    });
    this.readerPageLoader = createReaderPageLoader({
      getAppRouter: () => this.appRouter ?? this.props.appRouter,
      getCurrentTabName: () => this.selectedTabName,
      getOptions: () => this.readerOptions ?? this.props.options,
      getPageData: () => this.pageData,
      getReaderTextController: this.getReaderTextController,
      getRenderedCallback: () => this.renderedCallback,
      isOwner: this.isCurrentReaderPageOwner,
      patchPageData: this.patchPageData,
      removeReaderWordPopUp,
      requestReaderPageRerender: () => {
        this.loadReaderPage();
      },
      setAppRouter: (appRouter) => {
        this.appRouter = appRouter;
      },
      setOptions: (options) => {
        this.readerOptions = options;
      },
      setPageData: this.setPageData,
      setRenderedCallback: (renderedCallback) => {
        this.renderedCallback = renderedCallback;
      },
      updateLayout: this.updateLayout,
    });
    this.dictionaryManager = createDictionaryManager({
      stores,
      sideBarWidth,
      getSideBarVisible: () => this.sideBarVisible,
      getSelectedTabName: () => this.selectedTabName,
      selectSidebarTab: this.showSidebarTab,
      toggleSidebar: async (retainPosition) => {
        await this.sidebarEffects?.toggleSidebar(retainPosition);
      },
      updateDictionaryPaneState: (dictionaryPaneState) => {
        this.setState({ dictionaryPaneState });
      },
      updateLayout: this.updateLayout,
      updateSmartDictionaryPaneState: (smartDictionaryPaneState) => {
        this.setState({ smartDictionaryPaneState });
      },
      isOwner: this.isCurrentReaderPageOwner,
      getDictionarySheetHeight: () => {
        // Under the sheet's tabs and the dictionary's languages, which stay
        // in view
        const sheet = document.querySelector(".sideBar.bottomSheet");
        const choice = sheet?.querySelector<HTMLElement>("#dictionaryChoice");
        const above = choice?.offsetHeight
          ? choice
          : sheet?.querySelector(".titleBar");
        const sheetHeight = this.bottomSheet() && bottomSheetHeight();
        return sheetHeight && sheet && above
          ? sheetHeight -
              (above.getBoundingClientRect().bottom -
                sheet.getBoundingClientRect().top)
          : undefined;
      },
      onWordLookedUp: (position) => {
        this.lookedUpPosition = position;
        this.revealLookedUpWord();
      },
    });
    this.sidebarEffects = createReaderPageSidebarEffects({
      getShowSideBarCloseHint: () => this.showSideBarCloseHint,
      getSideBarVisible: () => this.sideBarVisible,
      setSidebarHint: (sidebarHint) => {
        this.setState({
          sidebarHintText: sidebarHint?.text,
          sidebarHintTitle: sidebarHint?.title,
          sidebarHintVisible: Boolean(sidebarHint),
        });
      },
      setShowSideBarCloseHint: (showSideBarCloseHint) => {
        this.showSideBarCloseHint = showSideBarCloseHint;
      },
      setSideBarAutoToggled: (sideBarAutoToggled) => {
        this.sideBarAutoToggled = sideBarAutoToggled;
      },
      setSideBarVisible: (sideBarVisible) => {
        this.sideBarVisible = sideBarVisible;
        this.setState({ sideBarVisible });
      },
      showContentTab: this.showSelectedSidebarTab,
      showDictionaryIfVisible: () => {
        this.dictionaryManager?.showDictionaryIfVisible();
      },
      showSmartDictionaryIfAppropriate: () =>
        this.dictionaryManager?.showSmartDictionaryIfAppropriate(),
      updateLayout: this.updateLayout,
    });
    this.readerStreakShowDisposer = stores.user.onChange(
      (user) => user.readerStreakShow,
      () => {
        this.loadStreakState();
      }
    );
    this.streakGoalMetDisposer = stores.streak.events.on(
      "goalMet",
      this.handleStreakGoalMet
    );
    this.initializePageNavigationControls();
    window.addEventListener("resize", this.updateViewportWidth);
    // The iOS app's dictionary sheet is the Dictionary tab's content, so the
    // user closing it closes the reader's sheet too
    window.READLANG.nativeDictionaryClosed = () => {
      if (this.sideBarVisible) {
        this.sidebarEffects?.toggleSidebar();
      }
    };
    this.loadReaderPage();
  }

  componentDidUpdate(previousProps: PreactPageProps, previousState: State) {
    // Each route dispatch builds a fresh options object (see app.ts), so a
    // changed identity means the user navigated - including to the position
    // already showing, which needs no page load but must still move the
    // video.
    if (previousProps.options !== this.props.options) {
      // The word the explain nudge points at has left the screen
      this.sidebarEffects?.hideExplainNudge();
      if (
        renderOptionsKey(previousProps.options) !==
        renderOptionsKey(this.props.options)
      ) {
        this.loadReaderPage();
      }
      this.syncAudio?.positionRequested(
        previousProps.options,
        this.props.options
      );
    }

    if (previousState.sideBarVisible !== this.state.sideBarVisible) {
      this.revealLookedUpWord();
    }

    // The iOS app's dictionary sheet is the Dictionary tab's content
    if (dictionaryShown(previousState) && !dictionaryShown(this.state)) {
      iOSBridge.get()?.closeDictionary();
    }

    if (previousState.pageData !== this.state.pageData) {
      this.updateDocumentTitle();
      this.loadStreakState();

      const bookID = this.state.pageData?.book?._id;
      if (bookID && bookID !== previousState.pageData?.book?._id) {
        // Load eagerly (not on first Guide-tab open) so that glossary-term
        // clicks in the text can be intercepted as soon as the page renders
        stores.guide.loadForBook(bookID);

        // e.g. arriving via the review page's "full plot summary" link
        if (stores.guide.consumeOpenTabRequest()) {
          void this.openGuideTab();
        }
      }
    }

    if (
      this.state.selectedTabName === "editWord" &&
      (previousState.selectedTabName !== "editWord" ||
        previousState.pageData?.book?._id !== this.state.pageData?.book?._id)
    ) {
      this.loadEditWordPane();
    }
  }

  componentWillUnmount() {
    this.unmounting = true;
    iOSBridge.get()?.closeDictionary();
    delete window.READLANG.nativeDictionaryClosed;
    stores.guide.stopPolling();
    this.removeSyncAudio();
    this.closeReaderTextInteractions();
    this.readerPageLoader?.close();
    this.readerPageLoader = undefined;
    this.removeReadAloudController();
    this.sidebarEffects?.cleanup();
    this.sidebarEffects = undefined;
    this.dictionaryManager?.cleanup();
    this.dictionaryManager = undefined;
    this.textController = undefined;
    this.readerTextStore = undefined;
    this.readerStreakShowDisposer?.();
    this.readerStreakShowDisposer = undefined;
    this.streakGoalMetDisposer?.();
    this.streakGoalMetDisposer = undefined;
    this.removePageNavigationControls();
    window.removeEventListener("resize", this.updateViewportWidth);
  }

  /**
   * Queues a page load behind any load already running, so that two loads can
   * never interleave their teardown, render and layout steps. `prepare` (the
   * re-initialisation teardown) runs in the same queue slot, immediately
   * before the load it belongs to.
   */
  loadReaderPage(prepare?: () => void): Promise<void> {
    return this.queueReaderPageLoad((isCurrent) => {
      if (this.unmounting) {
        return;
      }
      prepare?.();
      return this.runReaderPageLoad(isCurrent);
    });
  }

  private async runReaderPageLoad(isCurrent: () => boolean) {
    // A load which has been superseded, or whose page has gone away, leaves
    // the rest to the load that replaced it.
    const superseded = () => !isCurrent() || this.unmounting;

    if (!this.readerPageLoader) {
      return;
    }

    const preparedRender = await this.readerPageLoader.preparePreactRender(
      this.props.renderedCallback,
      this.props.appRouter,
      this.props.options
    );

    if (superseded()) {
      return;
    }

    if (preparedRender.cancelled) {
      return;
    }

    if (preparedRender.errorMessageLines) {
      this.pageData = undefined;
      this.setState(
        {
          errorMessageLines: preparedRender.errorMessageLines,
          pageData: undefined,
        },
        () => this.props.renderedCallback?.()
      );
      return;
    }

    this.pageData = preparedRender.pageData;
    this.readerTextStore = preparedRender.currentReaderTextStore;
    this.selectedTabName = selectedTabNameFromPageData(preparedRender.pageData);
    // A new page (or the page laid out afresh): no longer moved up to reveal
    // the word last looked up above the bottom sheet
    this.lookedUpPosition = undefined;
    await new Promise<void>((resolve) => {
      this.setState(
        {
          bookShift: undefined,
          errorMessageLines: undefined,
          pageData: preparedRender.pageData,
          readerTextStore: preparedRender.currentReaderTextStore,
          readerViewStore: preparedRender.currentReaderViewStore,
          scrollViewCompleted: false,
          selectedTabName: this.selectedTabName,
        },
        resolve
      );
    });

    if (superseded() || !this.readerPageLoader) {
      return;
    }

    await this.readerPageLoader.completePreactRender(
      preparedRender,
      this.getCompletePreactRenderCallbacks()
    );
  }

  render() {
    if (this.state.errorMessageLines) {
      return (
        <>
          {this.state.errorMessageLines.map((line) => (
            <p style={{ margin: "15px" }}>{line}</p>
          ))}
          <p style={{ margin: "15px" }}>
            <a href="/">Return Home</a>
          </p>
        </>
      );
    }

    if (!this.state.pageData) {
      return null;
    }

    return (
      <ReaderPageLayoutView
        appRouter={this.props.appRouter}
        bookShift={this.state.bookShift}
        bottomSheet={this.bottomSheet()}
        bottomSheetHeight={this.state.bottomSheetHeight}
        dictionaryPaneState={this.state.dictionaryPaneState}
        footerState={this.state.footerState}
        formattingOptionsProps={this.state.formattingOptionsProps}
        mediaPanelHeight={this.state.mediaPanelHeight}
        mediaPanelResizing={this.state.mediaPanelResizing}
        titleBarVisible={this.state.titleBarVisible}
        onDeleteAudio={() => {
          this.deleteAudio();
        }}
        onRemoveYouTube={() => {
          this.syncAudio?.removeYouTube();
        }}
        onSaveYouTubeID={(userInput) => {
          this.syncAudio?.saveYouTubeID(userInput);
        }}
        onSubmitYouTubeTranscript={async (transcript, mode) => {
          const result = await this.syncAudio?.submitYouTubeTranscript(
            transcript,
            mode
          );
          if (result?.textReplaced) {
            await this.refreshAfterEditDocument({ reInitialize: true });
          }
        }}
        onSectionChange={(start: number) => {
          readerPageNavigation.goToLocation(
            "from",
            start,
            this.appRouter ?? this.props.appRouter
          );
        }}
        onDismissSidebarHint={() => {
          this.sidebarEffects?.dismissSidebarHint({ dismissedByUser: true });
        }}
        onEditDocumentRefresh={(options) => {
          this.refreshAfterEditDocument(options);
        }}
        onOpenDictionary={() => {
          this.dictionaryManager?.openExternalDictionary();
        }}
        onNextPage={() => {
          this.gotoNextPage();
        }}
        onPageNavigationFurthest={() => {
          this.goToFurthestReadLocation();
        }}
        onPageNavigationSpecific={() => {
          this.showSpecificLocationModal();
        }}
        onPageNavigationStart={() => {
          this.goToStart();
        }}
        onPageNumberClick={() => {
          this.showPageNavigationTooltip();
        }}
        onPreviousPage={() => {
          this.gotoPreviousPage();
        }}
        onSwipe={(direction) => {
          // The page on the side the finger moves away from
          if (direction === "left") {
            this.goToRightPageControl();
          } else {
            this.goToLeftPageControl();
          }
        }}
        onReadAloudSkipBackward={() => {
          this.readAloudController?.jumpBackward();
        }}
        onReadAloudSkipForward={() => {
          this.readAloudController?.jumpForward();
        }}
        onReadAloudToggle={() => {
          this.readAloudController?.getReadAloudButton()?.toggle();
        }}
        onMediaPanelHeightChange={(mediaPanelHeight) => {
          this.setState({ mediaPanelHeight }, () => {
            this.updateLayoutAfterMediaPanelResize();
          });
        }}
        onMediaPanelResizingChange={(mediaPanelResizing) => {
          this.setState({ mediaPanelResizing });
        }}
        onMediaPlayerElementChange={(mediaPlayerElement) => {
          this.mediaPlayerElement = mediaPlayerElement;
        }}
        onHomeClick={this.handleHomeClick}
        onMarkCompleted={this.markAsCompleted}
        onStreakReminderPromptClose={() => {
          this.setState({ streakReminderPrompt: false });
        }}
        onStartReviewSession={this.startReviewSession}
        onSourceLanguageChange={(languageCode) => {
          stores.bookModel.patchBook({ language: languageCode });
        }}
        onSidebarTabSelect={(tabName) => {
          this.showSidebarTab(tabName);
        }}
        onSidebarToggle={() => {
          this.sidebarEffects?.toggleSidebar();
        }}
        onOpenSettings={() => {
          // Unlike onSidebarTabSelect, also expands the sidebar if it's
          // collapsed — invoked from the dropdown menu's "Open Settings…".
          this.toggleTab("formattingOptions");
        }}
        pageData={{
          ...this.state.pageData,
          showStreak: this.state.showStreak,
          streakHideUponCompletion: this.state.streakHideUponCompletion,
          streakReminderPrompt: this.state.streakReminderPrompt,
        }}
        pageTextViewportNoFooter={this.state.pageTextViewportNoFooter}
        readerTextStore={this.readerTextStore}
        readerViewStore={this.state.readerViewStore}
        scrollViewCompleted={this.state.scrollViewCompleted}
        selectedTabName={this.state.selectedTabName}
        sidebarHintText={this.state.sidebarHintText}
        sidebarHintTitle={this.state.sidebarHintTitle}
        sidebarHintVisible={this.state.sidebarHintVisible}
        sideBarVisible={this.state.sideBarVisible}
        sideBarWidth={sideBarWidth}
        smartDictionaryPaneState={this.state.smartDictionaryPaneState}
        syncAudioControlsState={this.state.syncAudioControlsState}
      />
    );
  }

  private getCompletePreactRenderCallbacks() {
    return {
      initializeFormattingOptions: this.setupFormattingOptions,
      initializeReadAloud: this.initializeReadAloud,
      initializeSyncAudio: this.initializeSyncAudio,
      updateSyncAudioOptions: this.updateSyncAudioOptions,
    };
  }

  private isCurrentReaderPageOwner = (book = stores.bookModel.book.value) =>
    (book.ownerID != null &&
      stores.user.state.value._id.toString() === book.ownerID.toString()) ||
    stores.user.state.value._id.toString() === "50cb58dbf939c7397b000002";

  private updateLayout = (
    retainPosition?: number,
    doNotRemoveFirstLine?: boolean
  ) => {
    this.textController?.updateLayout(retainPosition, doNotRemoveFirstLine);
  };

  private updateLayoutAfterMediaPanelResize = () => {
    this.textController?.updateLayout();
  };

  private getReaderTextController = () => {
    if (!this.textController) {
      throw new Error("Reader text controller is not initialized");
    }
    return this.textController;
  };

  private setPageData = (pageData: any) => {
    this.pageData = pageData;
    this.setState({ pageData });
  };

  private patchPageData = (patch: Record<string, any>) => {
    this.setPageData({
      ...this.pageData,
      ...patch,
    });
  };

  private refreshAfterEditDocument = async (options?: {
    reInitialize?: boolean;
  }) => {
    await this.loadReaderPage(
      options?.reInitialize
        ? () => {
            this.removeSyncAudio();
            this.closeReaderTextInteractions();
            this.readerPageLoader?.close();
          }
        : undefined
    );
  };

  private deleteAudio = async () => {
    const options = this.readerOptions ?? this.props.options;
    if (
      !confirm(
        "Are you sure? This will permanently delete the audio from this text and cannot be undone."
      )
    ) {
      return;
    }
    await readlangClient.request(
      "DELETE",
      `/api/book/${options.bookID}/audio`,
      {}
    );
    window.location.reload();
  };

  private closeReaderTextInteractions = () => {
    this.textController?.close();

    const readAloudButton = this.readAloudController?.getReadAloudButton();
    if (readAloudButton?.isPlaying) {
      readAloudButton.toggle();
    }
    this.readAloudController?.setTTSPosition(undefined);

    removeReaderWordPopUp();
    this.dictionaryManager?.cleanup();
  };

  private initializeReadAloud = (readerViewStore: ReaderViewStore) => {
    this.removeReadAloudController();

    const readAloudController = createReadAloudController({
      stores,
      readerViewStore,
      getSentenceFromPosition: (position) =>
        this.readerTextStore?.getSentenceFromPosition(position) ?? [],
      options: this.readerOptions ?? this.props.options,
      setupFormattingOptions: this.setupFormattingOptions,
      updateFooterState: this.updateFooterState,
      clearAudioPositionHighlight: () => {
        this.textController?.clearAudioPositionHighlight();
      },
      highlightAudioPosition: (position) => {
        this.textController?.highlightAudioPosition(position);
      },
      onReadAloudPlaybackChange: (isActive) => {
        this.setState((state) =>
          state.formattingOptionsProps
            ? {
                formattingOptionsProps: {
                  ...state.formattingOptionsProps,
                  readAloudPlaybackActive: isActive,
                },
              }
            : null
        );
      },
      onGotoNextPage: this.gotoNextPage,
      onGotoPreviousPage: this.gotoPreviousPage,
    });

    this.readAloudController = readAloudController;
    browserTTSVoices.onChange(readAloudController.ttsVoiceChangedHandler);

    // Intentionally not awaited; voice initialization can lag the initial
    // reader render, and the footer updates when the controller is ready.
    readAloudController.addReadAloudButtonIfAppropriate().then(() => {
      if (this.unmounting || this.readAloudController !== readAloudController) {
        return;
      }
      this.updateFooterState({
        readAloudAvailable: Boolean(readAloudController.getReadAloudButton()),
      });
    });
  };

  private removeReadAloudController() {
    if (!this.readAloudController) {
      return;
    }

    browserTTSVoices.offChange(this.readAloudController.ttsVoiceChangedHandler);
    this.readAloudController.dispose();
    this.readAloudController = undefined;
  }

  private setupFormattingOptions = async () => {
    const bookLanguage = stores.bookModel.book.value.language;
    const transliteration = languageMap.map[bookLanguage].transliterations?.[0];

    const formattingOptionsProps: FormattingOptionsProps = {
      transliteration: transliteration
        ? utils.toTitleCase(transliteration)
        : undefined,
      showReaderReviewOption: readerReviewUtils.hasAccess(stores),
      restartTTSIfPlayingOrPlayDemo: () => {
        const readAloudButton = this.readAloudController?.getReadAloudButton();
        if (readAloudButton?.isPlaying) {
          this.readAloudController?.restartReadAloud();
          return;
        }

        // Route the demo through tts.play so the resolved voice (which may
        // be Azure) is honored instead of always using the browser demo.
        const voiceId = tts.getVoicePickerState({
          user: stores.user,
          feature: "readAloud",
          language: bookLanguage,
        }).selectedVoiceId;
        tts.previewVoice({
          user: stores.user,
          feature: "readAloud",
          language: bookLanguage,
          voiceId,
          kind: "voiceSelected",
        });
      },
      audioFileMode: stores.bookModel.book.value.audioFileID != null,
      bookLanguage,
      readAloudPlaybackActive: false,
    };

    await new Promise<void>((resolve) => {
      this.setState({ formattingOptionsProps }, resolve);
    });
  };

  private showPageNavigationTooltip = () => {
    if (this.showingPageNavigationTooltip) {
      return;
    }

    const tooltipElement = document.querySelector<HTMLElement>(
      ".pageNavigationTooltip"
    );
    if (!tooltipElement) {
      return;
    }

    this.showingPageNavigationTooltip = true;

    modalController.showLegacy(tooltipElement, {
      center: false,
      closeButton: false,
      title: "Navigate to...",
      cancelCallback: () => {
        this.showingPageNavigationTooltip = false;
      },
    });
  };

  private goToStart = () => {
    this.readAloudController?.pauseReadAloud(true);
    modalController.cancelAll();
    readerPageNavigation.goToLocation(
      "from",
      0,
      this.appRouter ?? this.props.appRouter
    );
  };

  private goToFurthestReadLocation = () => {
    this.readAloudController?.pauseReadAloud(true);
    modalController.cancelAll();
    readerPageNavigation.goToLocation(
      "to",
      this.pageData?.furthestPosition,
      this.appRouter ?? this.props.appRouter
    );
  };

  private showSpecificLocationModal = () => {
    this.readAloudController?.pauseReadAloud(true);
    readerPageNavigation.showSpecificLocationModal(
      this.appRouter ?? this.props.appRouter,
      this.state.readerViewStore
    );
  };

  private initializePageNavigationControls = () => {
    this.updateFooterState({
      disableMarginButtons: stores.user.hasFeature("disableMarginButtons"),
    });

    keyboardShortcuts.registerHandler(
      "readerPage",
      keyboardShortcuts.keyCodes.left,
      this.goToLeftPageControl
    );
    keyboardShortcuts.registerHandler(
      "readerPage",
      keyboardShortcuts.keyCodes.right,
      this.goToRightPageControl
    );
    keyboardShortcuts.registerHandler(
      "readerPage",
      keyboardShortcuts.keyCodes.pageUp,
      this.goToLeftPageControl
    );
    keyboardShortcuts.registerHandler(
      "readerPage",
      keyboardShortcuts.keyCodes.pageDown,
      this.goToRightPageControl
    );
    keyboardShortcuts.registerHandler(
      "readerPage",
      keyboardShortcuts.keyCodes.escape,
      this.closeWordPopUp
    );

    keyboardShortcuts.registerHandler("readerPage", "e".charCodeAt(0), () => {
      this.toggleTab("smartDictionary");
    });
    keyboardShortcuts.registerHandler("readerPage", "E".charCodeAt(0), () => {
      this.toggleTab("smartDictionary");
    });
    keyboardShortcuts.registerHandler("readerPage", "d".charCodeAt(0), () => {
      this.toggleTab("dictionary");
    });
    keyboardShortcuts.registerHandler("readerPage", "D".charCodeAt(0), () => {
      this.toggleTab("dictionary");
    });
    keyboardShortcuts.registerHandler("readerPage", "w".charCodeAt(0), () => {
      this.toggleTab("editWord");
    });
    keyboardShortcuts.registerHandler("readerPage", "W".charCodeAt(0), () => {
      this.toggleTab("editWord");
    });
  };

  private removePageNavigationControls = () => {
    for (const keyCode of [
      keyboardShortcuts.keyCodes.left,
      keyboardShortcuts.keyCodes.right,
      keyboardShortcuts.keyCodes.pageUp,
      keyboardShortcuts.keyCodes.pageDown,
      keyboardShortcuts.keyCodes.escape,
    ]) {
      keyboardShortcuts.unregisterHandler("readerPage", keyCode, true);
    }

    for (const keyCode of [
      "e".charCodeAt(0),
      "E".charCodeAt(0),
      "d".charCodeAt(0),
      "D".charCodeAt(0),
      "w".charCodeAt(0),
      "W".charCodeAt(0),
    ]) {
      keyboardShortcuts.unregisterHandler("readerPage", keyCode);
    }
  };

  private updateFooterState = (footerState: ReaderFooterState) => {
    this.setState((previousState) => ({
      footerState: {
        ...previousState.footerState,
        ...footerState,
      },
    }));
  };

  private closeWordPopUp = () => {
    document
      .querySelector<HTMLElement>(".yourWordPopUp .closeDialogButton")
      ?.click();
  };

  private canUsePageControls = () =>
    stores.bookModel.book.value.youTubeID == null;

  private isReaderRTL = () =>
    languageMap.map[stores.bookModel.book.value.language].direction === "RTL";

  private goToLeftPageControl = () => {
    if (!this.canUsePageControls()) {
      return;
    }

    if (this.isReaderRTL()) {
      this.gotoNextPage();
    } else {
      this.gotoPreviousPage();
    }
  };

  private goToRightPageControl = () => {
    if (!this.canUsePageControls()) {
      return;
    }

    if (this.isReaderRTL()) {
      this.gotoPreviousPage();
    } else {
      this.gotoNextPage();
    }
  };

  private pauseReadAloudForPageTurn(keepAudioPlaying?: boolean) {
    if (keepAudioPlaying) {
      return;
    }

    const readAloudButton = this.readAloudController?.getReadAloudButton();
    if (readAloudButton?.isPlaying) {
      this.readAloudController?.pauseReadAloud();
    }
    this.readAloudController?.setTTSPosition(undefined);
  }

  private beforePageTurn(keepAudioPlaying?: boolean) {
    this.dictionaryManager?.setPreviousUserWord(undefined);
    this.dictionaryManager?.showSmartDictionaryIfAppropriate();
    this.pauseReadAloudForPageTurn(keepAudioPlaying);
  }

  private gotoPreviousPage = (keepAudioPlaying?: boolean) => {
    if (!this.textController) {
      return;
    }

    const options = this.readerOptions ?? this.props.options;
    const appRouter = this.appRouter ?? this.props.appRouter;
    this.beforePageTurn(keepAudioPlaying);

    if (options.endOfText) {
      appRouter.navigate(
        "library/" +
          options.bookID +
          "/to/" +
          stores.bookModel.book.value.totalWords,
        {
          trigger: true,
        }
      );
      return;
    }

    if (!this.textController.areNavigationButtonsEnabled()) {
      return;
    }

    const firstSpanStartPosition =
      this.textController.getFirstSpanStartPosition();
    if (firstSpanStartPosition <= 0) {
      notificationBar.showMessage({
        message: "You're on the first page.",
        element: $(".container"),
      });
      return;
    }

    delete options.from;
    options.to = Math.max(0, firstSpanStartPosition - 1);

    this.updateFooterState({ previousActivated: true });
    this.renderedCallback = this.textController.getSavedRenderedCallback();
    this.state.readerViewStore?.setPageTurn("previous");
    appRouter.navigate("library/" + options.bookID + "/to/" + options.to, {
      trigger: true,
    });
  };

  private gotoNextPage = async (keepAudioPlaying = false) => {
    if (!this.textController) {
      return;
    }

    const options = this.readerOptions ?? this.props.options;
    const appRouter = this.appRouter ?? this.props.appRouter;
    walkthrough.setAchievement("turnPage", true);

    this.beforePageTurn(keepAudioPlaying);

    if (options.endOfText) {
      return;
    }

    if (
      !(await readlangClient.isLocalMode()) &&
      !stores.bookModel.hasTag("queue") &&
      !stores.bookModel.hasTag("completed") &&
      !this.textController.onLastPage()
    ) {
      stores.bookModel.addTag("queue");
    }

    await this.textController.trackFinishPage();

    if (!this.textController.areNavigationButtonsEnabled()) {
      return;
    }
    if (this.textController.onLastPage()) {
      await this.markAsCompleted();
      return;
    }
    if (this.textController.isAtEndOfBookLastSpan()) {
      notificationBar.showMessage({
        message: "You're on the last page.",
        element: $(".container"),
      });
      return;
    }

    delete options.to;
    options.from = this.textController.getLastSpanEndPosition() + 1;

    this.updateFooterState({ nextActivated: true });
    this.renderedCallback = this.textController.getSavedRenderedCallback();
    this.state.readerViewStore?.setPageTurn("next");
    appRouter.navigate("library/" + options.bookID + "/from/" + options.from, {
      trigger: true,
    });
  };

  /** Opens the sidebar if needed and shows the Guide tab */
  private openGuideTab = async () => {
    if (!this.sideBarVisible) {
      await this.sidebarEffects?.toggleSidebar();
    }
    if (this.selectedTabName !== "guide") {
      await this.showSidebarTab("guide");
    }
  };

  private toggleTab = async (tabName: string) => {
    if (!this.sidebarEffects) {
      return;
    }

    if (this.sideBarVisible && this.selectedTabName === tabName) {
      this.sidebarEffects.toggleSidebar();
      return;
    }

    if (!this.sideBarVisible) {
      await this.sidebarEffects.toggleSidebar();
    }

    if (this.selectedTabName !== tabName) {
      await this.showSidebarTab(tabName);
    }
  };

  private handleHomeClick: preact.JSX.MouseEventHandler<HTMLAnchorElement> = (
    event
  ) => {
    if (
      this.textController?.onLastPage() &&
      !stores.bookModel.hasTag("completed") &&
      stores.bookModel.book.value.youTubeID == null &&
      !confirm(
        "Are you sure you want to leave before continuing to the last page? Click OK to leave " +
          "or Cancel to stay."
      )
    ) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    // Back to the page it was opened from, as left (rather than opening it again)
    if (pageTransitions.canGoBack()) {
      event.stopPropagation();
      event.preventDefault();
      pageTransitions.goBack();
    }
  };

  private startReviewSession = (toPosition: number) => {
    const readerViewStore = this.state.readerViewStore;
    if (!readerViewStore) {
      return;
    }

    const options = this.readerOptions ?? this.props.options;
    const firstSpanPosition = readerViewStore.firstPosition.value ?? 0;
    (this.appRouter ?? this.props.appRouter).navigate(
      `/library/${options.bookID}/review/${firstSpanPosition}/${toPosition}`,
      { trigger: true }
    );
  };

  private markAsCompleted = async () => {
    // Scroll-mode texts swap in the end-of-text panel without navigating
    this.sidebarEffects?.hideExplainNudge();
    await this.textController?.trackFinishPage();

    if (await readlangClient.isLocalMode()) {
      signupModal.show({
        heading: "Sign in to manage your texts",
        stores,
      });
      return;
    }

    stores.bookModel.addTag("completed");

    if (stores.bookModel.book.value.youTubeID != null) {
      // Scroll-mode (YouTube) texts stay on the page; the text island swaps
      // its scroll footer to the end-of-text panel.
      this.setState({ scrollViewCompleted: true });
      return;
    }

    const options = this.readerOptions ?? this.props.options;
    const appRouter = this.appRouter ?? this.props.appRouter;
    appRouter.navigate("library/" + options.bookID + "/end", {
      trigger: true,
      replace: true,
    });
  };

  private initializeSyncAudio = (readerViewStore: ReaderViewStore) => {
    this.removeSyncAudio();

    this.syncAudio = new ReaderSyncAudio({
      readerViewStore,
      getReaderTextStore: () => this.readerTextStore,
      appRouter: this.appRouter ?? this.props.appRouter,
      options: this.readerOptions ?? this.props.options,
      isOwner: this.isCurrentReaderPageOwner,
      markAsCompleted: this.markAsCompleted,
      getMediaPlayerElement: () => this.mediaPlayerElement,
      setControlsState: this.setSyncAudioControlsState,
      setMediaPanelHeight: (mediaPanelHeight, callback) => {
        this.setState({ mediaPanelHeight }, callback);
      },
      setPageTextViewportNoFooter: (pageTextViewportNoFooter) => {
        this.setState({ pageTextViewportNoFooter });
      },
      updateLayout: this.updateLayoutAfterMediaPanelResize,
      updateFooterState: this.updateFooterState,
      updateUserBookPosition: (position) => {
        this.textController?.updateSyncAudioUserBookPosition(position);
      },
      onReadingPosition: (position) => {
        this.textController?.trackSyncAudioPosition(position);
      },
    });
    this.syncAudio.initialize();
  };

  private updateSyncAudioOptions = (options: any) => {
    this.syncAudio?.updateOptions(options);
  };

  private removeSyncAudio() {
    this.syncAudio?.remove();
    this.syncAudio = undefined;
    if (!this.unmounting) {
      this.setState({
        mediaPanelHeight: undefined,
        pageTextViewportNoFooter: false,
        syncAudioControlsState: undefined,
      });
    }
  }

  private setSyncAudioControlsState = (
    syncAudioControlsState: SyncAudioControlsProps | undefined
  ) => {
    if (this.unmounting) {
      return;
    }
    this.setState({ syncAudioControlsState });
  };

  private bottomSheet = () =>
    (this.state.viewportWidth ?? window.innerWidth) <= bottomSheetThreshold;

  /**
   * Moves the text up so the word last looked up stays in view above the
   * bottom sheet, for its explanation to be read alongside, and back down as
   * the sheet closes
   */
  private revealLookedUpWord = () => {
    const currentShift = this.state.bookShift ?? 0;
    const sheetHeight =
      this.sideBarVisible && this.bottomSheet()
        ? bottomSheetHeight()
        : undefined;
    const bookShift =
      sheetHeight !== undefined && this.lookedUpPosition !== undefined
        ? shiftToRevealWord({
            wordElement: elementForPosition(
              this.readerTextStore,
              this.lookedUpPosition
            ),
            currentShift,
            sheetHeight,
          })
        : 0;
    if (sheetHeight !== undefined) {
      this.setState({ bottomSheetHeight: sheetHeight });
    }
    if (bookShift !== currentShift) {
      // Pop-ups over the text are placed for where it was
      removeReaderWordPopUp();
      this.setState({ bookShift });
    }
  };

  private updateViewportWidth = () => {
    // The looked-up word kept in view above a sheet resized (a phone turned),
    // or the text put back as the sheet becomes a sidebar
    this.setState(
      { viewportWidth: window.innerWidth },
      this.revealLookedUpWord
    );
    this.sidebarEffects?.handleResize();
  };

  private autoExpandSidebarIfAppropriate = () => {
    if (
      this.sideBarAutoToggled ||
      !sidebarSettings.persistentMode() ||
      !sidebarSettings.isExpanded(stores.user)
    ) {
      return;
    }

    this.sidebarEffects?.toggleSidebar();
  };

  private showSidebarTab = async (tabName: string) => {
    this.selectedTabName = tabName;
    await new Promise<void>((resolve) => {
      this.setState({ selectedTabName: tabName }, resolve);
    });
    await this.showSelectedSidebarTab();
  };

  private showSelectedSidebarTab = async () => {
    const selectedTab = this.selectedTabName ?? sidebarSettings.getTab();
    sidebarSettings.setTab(selectedTab);

    this.dictionaryManager?.showDictionaryIfVisible();
    this.dictionaryManager?.showSmartDictionaryIfAppropriate();

    analytics.trackEventWithProperties("showSidebarTab", {
      tab: selectedTab,
    });
  };

  private async loadEditWordPane() {
    const bookID = this.state.pageData?.book?._id;
    if (!bookID || (await localAPI.isLocalMode())) {
      return;
    }

    stores.remoteUserWords.fetch({
      data: {
        bookID,
      },
    });
  }

  private handleStreakGoalMet = async () => {
    if (await streakReminderPrompt.claimAtGoalMet(stores)) {
      this.setState({ streakReminderPrompt: true });
    }
  };

  private async loadStreakState() {
    if (!this.state.pageData) {
      return;
    }

    if (!(await stores.user.hasNewStreakFeature())) {
      this.setState({
        showStreak: false,
        streakHideUponCompletion: false,
      });
      return;
    }

    await stores.streak.fetch();

    const goalMetToday = stores.streak.todayStats?.goalMet;
    const streakShow = stores.user.state.value.readerStreakShow ?? "always";

    if (
      streakShow === "always" ||
      (streakShow === "untilComplete" && !goalMetToday)
    ) {
      this.setState({
        showStreak: true,
        streakHideUponCompletion: streakShow === "untilComplete",
      });
    } else {
      this.setState({
        showStreak: false,
        streakHideUponCompletion: false,
      });
    }
  }

  private updateDocumentTitle = () => {
    const book = this.state.pageData?.book;
    if (!book) {
      return;
    }

    if (this.state.pageData?.localMode && book.language) {
      const languageString = languageMap.map[book.language]?.english;
      if (languageString) {
        document.title = `Learn ${languageString} with "${book.title}"`;
        return;
      }
    }

    document.title = `${book.title} - Readlang`;
  };
}

const dictionaryShown = (state: State) =>
  state.sideBarVisible && state.selectedTabName === "dictionary";

const selectedTabNameFromPageData = (pageData?: any): string | undefined =>
  pageData?.selectedTabName;
